/* =========================================================================
   Unit Conversion System for Telemetry Dashboard
   ========================================================================= */

import { TelemetryCatChannelType, TelemetryUnitSystem } from '../types/telemetry-dashboard.types';
import { resolveEnumValue } from './telemetry-enum.utils';

interface UnitEntry {
  label: string;
  from: (v: number) => number;
}

interface UnitGroup {
  label: string;
  units: Record<string, UnitEntry>;
}

export const UNITS: Record<string, UnitGroup> = {
  temp: {
    label: 'Temperature',
    units: {
      C: { label: '°C', from: (v) => v },
      F: { label: '°F', from: (v) => v * 9 / 5 + 32 },
      K: { label: 'K', from: (v) => v + 273.15 },
    },
  },
  angle: {
    label: 'Angle',
    units: {
      deg: { label: 'deg', from: (v) => v },
      rad: { label: 'rad', from: (v) => v * Math.PI / 180 },
    },
  },
  angrate: {
    label: 'Angular Rate',
    units: {
      degs: { label: 'deg/s', from: (v) => v },
      rads: { label: 'rad/s', from: (v) => v * Math.PI / 180 },
      rpm: { label: 'RPM', from: (v) => v / 6 },
    },
  },
  rate: {
    label: 'Rate',
    units: {
      kms: { label: 'km/s', from: (v) => v },
      ms: { label: 'm/s', from: (v) => v * 1000 },
    },
  },
  distance: {
    label: 'Distance',
    units: {
      cm: { label: 'cm', from: (v) => v * 100 },
      m: { label: 'm', from: (v) => v },
      km: { label: 'km', from: (v) => v / 1000 },
    },
  },
};

export const DEFAULT_UNITS: TelemetryUnitSystem = {
  temp: 'C',
  angle: 'deg',
  angrate: 'degs',
  rate: 'kms',
  distance: 'm',
};

/**
 * Resolves the unitType category ('temp' | 'angle' | 'angrate' | 'distance')
 * either from def.unitType or by dynamically matching def.unit / paramName.
 */
export function resolveUnitType(def?: TelemetryCatChannelType | null): string {
  if (!def) return '';
  if (def.unitType && typeof def.unitType === 'string' && def.unitType.trim()) {
    const ut = def.unitType.trim().toLowerCase();
    if (ut === 'temperature') return 'temp';
    if (ut === 'angularrate' || ut === 'angular_rate') return 'angrate';
    if (ut === 'rate' || ut === 'velocity' || ut === 'speed') return 'rate';
    if (ut in UNITS) return ut;
  }

  const rawUnit = String(def.unit || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const paramKey = String(def.paramName || def.paramId || def.label || '').toLowerCase();

  // Temperature matching: unit is °C, C, °F, F, K, degC, etc.
  if (
    rawUnit === '°c' ||
    rawUnit === 'c' ||
    rawUnit === 'degc' ||
    rawUnit === 'deg_c' ||
    rawUnit === 'deg c' ||
    rawUnit === '° c' ||
    rawUnit === 'celsius' ||
    rawUnit === '°f' ||
    rawUnit === 'f' ||
    rawUnit === 'degf' ||
    rawUnit === 'deg_f' ||
    rawUnit === 'deg f' ||
    rawUnit === '° f' ||
    rawUnit === 'fahrenheit' ||
    rawUnit === 'k' ||
    rawUnit === 'kelvin'
  ) {
    return 'temp';
  }

  // Angular rate matching: deg/s, rad/s, rpm, °/s, etc. (Check before angle)
  if (
    rawUnit === 'deg/s' ||
    rawUnit === 'deg/sec' ||
    rawUnit === 'deg / s' ||
    rawUnit === 'deg / sec' ||
    rawUnit === 'degs' ||
    rawUnit === '°/s' ||
    rawUnit === '°/sec' ||
    rawUnit === '° / s' ||
    rawUnit === 'rad/s' ||
    rawUnit === 'rad/sec' ||
    rawUnit === 'rad / s' ||
    rawUnit === 'rad / sec' ||
    rawUnit === 'rads' ||
    rawUnit === 'rpm' ||
    rawUnit === 'dps'
  ) {
    return 'angrate';
  }

  // Linear rate / velocity matching: km/s, m/s, km/sec, m/sec, etc. (Check before angle and distance)
  if (
    rawUnit === 'km/s' ||
    rawUnit === 'km/sec' ||
    rawUnit === 'km / s' ||
    rawUnit === 'km / sec' ||
    rawUnit === 'kilometer/s' ||
    rawUnit === 'kilometers/s' ||
    rawUnit === 'm/s' ||
    rawUnit === 'm/sec' ||
    rawUnit === 'm / s' ||
    rawUnit === 'm / sec' ||
    rawUnit === 'meter/s' ||
    rawUnit === 'meters/s'
  ) {
    return 'rate';
  }

  // Angle matching: deg, rad, degrees, °
  if (
    rawUnit === 'deg' ||
    rawUnit === 'degree' ||
    rawUnit === 'degrees' ||
    rawUnit === '°' ||
    rawUnit === 'rad' ||
    rawUnit === 'radian' ||
    rawUnit === 'radians'
  ) {
    return 'angle';
  }

  // Distance matching: cm, m, km
  if (
    rawUnit === 'm' ||
    rawUnit === 'meter' ||
    rawUnit === 'meters' ||
    rawUnit === 'km' ||
    rawUnit === 'kilometer' ||
    rawUnit === 'kilometers' ||
    rawUnit === 'cm' ||
    rawUnit === 'centimeter'
  ) {
    return 'distance';
  }

  // Heuristic fallbacks based on parameter naming ONLY if rawUnit is empty or 'none'
  // (Prevents false positives on non-unit fields like error_count, attempt, voltage, etc.)
  if (isNoneUnit(rawUnit)) {
    if (/(^|[_\W])(temperature|temp)([_\W]|$)/i.test(paramKey)) {
      return 'temp';
    }
    if (/(^|[_\W])(angrate|angular_rate|gyro|gyroscope|omega|roll_rate|pitch_rate|yaw_rate|ang_vel)([_\W]|$)/i.test(paramKey)) {
      return 'angrate';
    }
    if (/(^|[_\W])(angle|azimuth|elevation|roll|pitch|yaw)([_\W]|$)/i.test(paramKey)) {
      return 'angle';
    }
    if (/(^|[_\W])(velocity|speed|rate_linear|linear_rate)([_\W]|$)/i.test(paramKey)) {
      return 'rate';
    }
  }

  return '';
}

/**
 * Convert a raw value through the active unit system.
 */
export function conv(v: number | null | undefined, def: TelemetryCatChannelType, units: TelemetryUnitSystem): number | null {
  if (v == null) return null;
  if (!def) return v;

  // Only bypass numeric conversion if the channel has defined enum mappings AND the value resolves to an enum
  if (def.enumValues && typeof def.enumValues === 'object' && Object.keys(def.enumValues).length > 0) {
    if (resolveEnumValue(v, def.enumValues) !== null) {
      return v;
    }
  }

  const unitType = resolveUnitType(def);
  if (!unitType || !(unitType in UNITS)) return v;

  const g = UNITS[unitType];
  const targetUnitKey = (units as any)?.[unitType] || (DEFAULT_UNITS as any)[unitType] || Object.keys(g.units)[0];
  const rawUnit = String(def.unit || '').trim().toLowerCase().replace(/\s+/g, ' ');

  // Normalize incoming value to standard base SI unit
  let baseVal = typeof v === 'number' ? v : Number(v);
  if (isNaN(baseVal)) return v;

  if (unitType === 'temp') {
    if (
      rawUnit === '°f' ||
      rawUnit === 'f' ||
      rawUnit === 'degf' ||
      rawUnit === 'deg_f' ||
      rawUnit === 'deg f' ||
      rawUnit === '° f' ||
      rawUnit === 'fahrenheit'
    ) {
      baseVal = ((baseVal - 32) * 5) / 9;
    } else if (rawUnit === 'k' || rawUnit === 'kelvin') {
      baseVal = baseVal - 273.15;
    }
  } else if (unitType === 'angle') {
    if (rawUnit === 'rad' || rawUnit === 'radian' || rawUnit === 'radians') {
      baseVal = (baseVal * 180) / Math.PI;
    }
  } else if (unitType === 'angrate') {
    if (
      rawUnit === 'rad/s' ||
      rawUnit === 'rad/sec' ||
      rawUnit === 'rad / s' ||
      rawUnit === 'rad / sec' ||
      rawUnit === 'rads'
    ) {
      baseVal = (baseVal * 180) / Math.PI;
    } else if (rawUnit === 'rpm') {
      baseVal = baseVal * 6;
    }
  } else if (unitType === 'rate') {
    if (
      rawUnit === 'm/s' ||
      rawUnit === 'm/sec' ||
      rawUnit === 'm / s' ||
      rawUnit === 'm / sec' ||
      rawUnit === 'meter/s' ||
      rawUnit === 'meters/s'
    ) {
      baseVal = baseVal / 1000;
    }
  } else if (unitType === 'distance') {
    if (rawUnit === 'km' || rawUnit === 'kilometer' || rawUnit === 'kilometers') {
      baseVal = baseVal * 1000;
    } else if (rawUnit === 'cm' || rawUnit === 'centimeter') {
      baseVal = baseVal / 100;
    }
  }

  return g.units[targetUnitKey] ? g.units[targetUnitKey].from(baseVal) : baseVal;
}

/**
 * Checks if a unit string represents an empty, non-existent, or 'none' unit.
 */
export function isNoneUnit(u?: string | null): boolean {
  if (!u) return true;
  const s = String(u).trim().toLowerCase();
  return s === '' || s === 'none' || s === 'nil' || s === 'null' || s === 'undefined' || s === 'unknown';
}

/**
 * Returns a cleaned unit string, or empty string if unit is empty or represents 'none'.
 */
export function cleanUnit(u?: string | null): string {
  return isNoneUnit(u) ? '' : String(u).trim();
}

/**
 * Resolve display unit label for a channel based on active unit system.
 * Returns empty string if the unit represents 'none'.
 */
export function unitOf(def: TelemetryCatChannelType, units: TelemetryUnitSystem): string {
  if (!def) return '';
  const unitType = resolveUnitType(def);
  if (unitType && (UNITS as any)[unitType]) {
    const g = (UNITS as any)[unitType];
    const uk = (units as any)?.[unitType] || (DEFAULT_UNITS as any)[unitType] || Object.keys(g.units)[0];
    if (g.units[uk]) {
      return g.units[uk].label;
    }
  }
  return cleanUnit(def.unit || '');
}

/**
 * Format a value with conversion and precision, resolving enumValues if defined.
 */
export function fmt(
  v: number | string | null | undefined,
  def: TelemetryCatChannelType,
  units: TelemetryUnitSystem = {},
  precision: number = 2
): string {
  if (v == null) return '—';
  if (typeof v === 'string' && v.trim() === '') return '—';
  const enumVal = resolveEnumValue(v, def?.enumValues);
  if (enumVal) {
    return `${enumVal} (${v})`;
  }
  const numVal = typeof v === 'number' ? v : Number(v);
  if (isNaN(numVal)) return String(v);
  const c = conv(numVal, def, units);
  if (c == null) return '—';
  return c.toFixed(precision);
}
